<script setup lang="ts">
import { twMerge } from "tailwind-merge";

import { SpotlightShapeNameOptions } from "~/telestrations/constants/colors";
import { TELESTRATION_TOOLTIP_CONTAINER } from "~/telestrations/constants/tooltip";
import { SpotlightShapeName } from "~/telestrations/types/canvas";

const { t } = useI18n();
const shapesStore = useShapesStore();
const isMenuOpened = ref(false);

const options = computed(() => {
  if (shapesStore.selectedShape) {
    return SpotlightShapeNameOptions[shapesStore.selectedShape.type].map((option) => ({
      ...option,
      label: t(option.labelKey),
    }));
  }

  return [];
});
const selectedShapeName = computed(() => {
  if (shapesStore.selectedShape) {
    return shapesStore.selectedShape.name as SpotlightShapeName;
  }

  return "rectangle";
});

const inputClasses = computed(() =>
  twMerge(
    "!border-none !gap-x-2 px-1.5",
    isMenuOpened.value ? "!bg-neutral-light-100 dark:!bg-neutral-dark-500" : "hover:!bg-neutral-light-100 dark:hover:!bg-neutral-dark-500"
  )
);

function onShow() {
  isMenuOpened.value = true;
}

function onHide() {
  isMenuOpened.value = false;
}

function onSpotlightShapeChange(value: string) {
  const selectedShape = shapesStore.selectedShape;

  if (!selectedShape || value === selectedShape.name) {
    return;
  }

  const scaleX = selectedShape.scaleX ?? 1;
  const scaleY = selectedShape.scaleY ?? 1;

  if (value === SpotlightShapeName.ELLIPSE) {
    const width = (selectedShape.width ?? 0) * scaleX;
    const height = (selectedShape.height ?? 0) * scaleY;
    const centerX = (selectedShape.x ?? 0) + width / 2;
    const centerY = (selectedShape.y ?? 0) + height / 2;
    const diameter = Math.max(width, height);

    shapesStore.updateShape(selectedShape, {
      width: undefined,
      height: undefined,
      x: centerX,
      y: centerY,
      radiusX: diameter / 2,
      radiusY: diameter / 2,
      scaleX: 1,
      scaleY: 1,
      name: value,
    });
  } else {
    const radiusX = (selectedShape.radiusX ?? 0) * scaleX;
    const radiusY = (selectedShape.radiusY ?? 0) * scaleY;
    const size = Math.max(radiusX, radiusY) * 2;
    const topLeftX = (selectedShape.x ?? 0) - size / 2;
    const topLeftY = (selectedShape.y ?? 0) - size / 2;

    shapesStore.updateShape(selectedShape, {
      radiusX: undefined,
      radiusY: undefined,
      x: topLeftX,
      y: topLeftY,
      width: size,
      height: size,
      scaleX: 1,
      scaleY: 1,
      name: value,
    });
  }
  shapesStore.applyTransformerSpotlightAnchorsConfig(selectedShape);
  nextTick(() => {
    shapesStore.attachTransformerToShape(selectedShape);
  });
}
</script>

<template>
  <template v-if="options.length">
    <USelectMenu
      :model-value="selectedShapeName"
      :options="options"
      :input-class="inputClasses"
      list-class="grid grid-cols-2 gap-x-2 gap-y-2 ps-1 pe-1"
      item-class="min-h-max rounded aria-selected:bg-neutral-light-100 dark:aria-selected:bg-neutral-dark-500"
      container-class="p-1"
      variant="inline"
      :divider="false"
      :hide-trailing-icon="true"
      :render-in-portal="false"
      @show="onShow"
      @hide="onHide"
      @update:model-value="onSpotlightShapeChange"
    >
      <template #option="{ option }">
        <UIcon class="size-6" :class="selectedShapeName === option.value ? 'text-blue-500' : ''" :name="option.value" />
      </template>
      <UIcon
        v-tooltip="{
          content: t('labels.change_spotlight_shape'),
          container: TELESTRATION_TOOLTIP_CONTAINER,
        }"
        class="size-6 text-black dark:text-white"
        :name="selectedShapeName"
      />
    </USelectMenu>
  </template>
</template>
